import { history } from '@yl/react-router';
import { ampli } from '../amplitude/index';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { detectLocale } from '../../external/shared/localization/languageDetector';
import { getVoAuthToken } from '../../app/authentication/VoAuthentication';

declare global {
  interface Window {
    __API_KEY__?: { ApiKey?: string };
    dataLayer: any[];
  }
}

export const dataLayer = window.dataLayer;

const apiKey = window.__API_KEY__?.ApiKey;
if (apiKey) {
  const amplitudeClient = amplitude.createInstance();

  const sessionReplay = sessionReplayPlugin({
    sampleRate: 1.0,
    debugMode: true,
    forceSessionTracking: true,
    privacyConfig: {
      unmaskSelector: ['.cls_unmask'],
      defaultMaskLevel: 'conservative'
    },
    captureDocumentTitle: true
  });

  amplitudeClient.add(sessionReplay);

  amplitudeClient
    .init(apiKey, undefined, {
      defaultTracking: {
        sessions: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false
      },
      sessionTimeout: 30 * 60 * 1000,
      logLevel: amplitude.Types.LogLevel.Warn
    })
    .promise.then(() => {
    console.log('[Session Recording] Amplitude initialized', {
      sessionId: amplitudeClient.getSessionId(),
      deviceId: amplitudeClient.getDeviceId()
    });
  })
    .catch(error => {
      console.error('[Session Recording] Failed to initialize Amplitude:', error);
    });

  ampli.load({
    client: {
      instance: amplitudeClient
    }
  });

  setTimeout(() => {
    ampli.pageView({
      PAGE_PATH: window.location.pathname + detectLocale(),
      PAGE_URL: window.location.href,
      LOGGED_IN_STATE: getVoAuthToken() ? 'LOGGED_IN' : 'LOGGED_OUT',
      PAGE_TITLE: document.title || window.location.href.substring(window.location.href.lastIndexOf('/') + 1),
      PAGE_QUERY_STRING: window.location.href.includes('?') ? window.location.href.split('?')[1] : ''
    });
  }, 100);
}
export function sendHistoryEventsToAnalytics() {
  history.listen(() => {
    dataLayer.push({
      event: 'content-view',
      'content-name': history.location.pathname
    });

    ampli.pageView({
      PAGE_PATH: window.location.pathname + detectLocale(),
      PAGE_URL: window.location.href,
      LOGGED_IN_STATE: getVoAuthToken() ? 'LOGGED_IN' : 'LOGGED_OUT',
      PAGE_TITLE: window.location.href.substring(window.location.href.lastIndexOf('/') + 1),
      PAGE_QUERY_STRING: window.location.href.includes('?') ? window.location.href.split('?')[1] : ''
    });
  });
}
export type ECommerceStatus =
  | 'successful-payment'
  | 'pending-payment'
  | 'failed-payment'
  | 'failed-request'
  | 'failed-unknown'
  | 'early-enrollment-completed';

export function publishECommerceCompletionEvent(status: ECommerceStatus, data?: CompletionAnalyticsEventData) {
  let event;
  if (data) {
    event = data.hasEr ? 'New Member Enrollment With ER' : 'New Member Enrollment Without ER';
  }
  dataLayer.push({
    event,
    status,
    sponsorId: data ? data.sponsorId : undefined,
    enrollerId: data ? data.enrollerId : undefined,
    memberType: data ? data.memberType : undefined,
    paymentType: data ? data.paymentType : undefined,
    ecommerce: {
      currencyCode: data ? data.currencyCode : undefined,
      purchase: {
        actionField: {
          coupon: '',
          affiliation: event,
          id: data ? data.orderId : undefined,
          revenue: data ? data.revenue : undefined,
          tax: data ? data.tax : undefined,
          shipping: data ? data.shipping : undefined
        },
        products: data ? data.products : undefined
      }
    }
  });
}

export interface CompletionAnalyticsEventData {
  hasEr: boolean;
  enrollerId: string;
  sponsorId: string;
  revenue: string;
  shipping: string;
  orderId: string;
  tax: string;
  currencyCode: string;
  memberType: string;
  paymentType: string;
  products: ECommerceProduct[];
}

export type AnalyticEventTypes = 'content-view';

export type EnrollmentCompletionEventType = 'New Member Enrollment With ER' | 'New Member Enrollment Without ER';

export type AnalyticsPaymentType = 'CreditCard';

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

export type ECommerceAnalyticsEventPartial = RecursivePartial<ECommerceAnalyticsEvent>;

export interface ECommerceActionField {
  coupon: string;
  affiliation: EnrollmentCompletionEventType;
  id: string;
  revenue: string;
  tax: string;
  shipping: string;
}

export interface ECommerceProduct {
  name: string;
  id: string;
  price: string;
  quantity: string;
}

export interface ECommercePurchase {
  actionField: ECommerceActionField;
}

export interface ECommerceAnalyticsEvent {
  currencyCode: string;
  purchase: ECommercePurchase;
}

export interface EnrollmentAnalyticsEvent {
  event: EnrollmentCompletionEventType;
  sponsorId: string;
  enrollerId: string;
  memberType: string;
  paymentType: AnalyticsPaymentType;
  ecommerce: ECommerceAnalyticsEvent;
}
